import { c as _c } from "react/compiler-runtime";
import { useCallback, useRef, useSyncExternalStore } from "react";
import { hashKey, notifyManager, replaceEqualDeep, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "../queryKeys";
import type { ActiveSessionData } from "@sukha/timer-core";
import type { QueryClient } from "@tanstack/react-query";
const ACTIVE_SESSION_KEY = QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[];
const ACTIVE_SESSION_HASH = hashKey(ACTIVE_SESSION_KEY);

/**
 * Subscribes to a narrow slice of the shared `["activeSession"]` cache entry
 * without registering a query observer (the owning `useActiveSession`
 * subscription is responsible for fetching and self-healing).
 *
 * Not implemented with `useQuery` + `skipToken` on purpose: observers of the
 * same query key share one query whose effective options come from the most
 * recently updated observer, so a skipToken observer can hijack the query's
 * `queryFn` and break invalidation-triggered refetches for everyone. Reading
 * the cache through `useSyncExternalStore` has no such interaction.
 *
 * The timer sync worker rewrites the cache entry on real news only, but any
 * write still notifies whole-object subscribers. Slice subscribers re-render
 * only when their selected value changes; deep-equal select results keep
 * their identity, so object-returning selects are safe.
 *
 * `select` must derive purely from the cache data. Prefer the field hooks
 * below; reach for this directly when a component needs a combination of
 * fields.
 */
export function useActiveSessionSlice<T>(select: (data: ActiveSessionData | null) => T): T {
  const queryClient = useQueryClient();
  const selectRef = useRef(select);
  selectRef.current = select;
  const memoRef = useRef<{
    input: ActiveSessionData | null | undefined;
    result: T;
  } | null>(null);
  const getSnapshot = useCallback((): T => {
    const data = queryClient.getQueryData<ActiveSessionData | null>(ACTIVE_SESSION_KEY);
    const memo = memoRef.current;
    if (memo && Object.is(memo.input, data)) {
      return memo.result;
    }
    const selected = selectRef.current(data ?? null);
    const result = memo ? replaceEqualDeep(memo.result, selected) : selected;
    memoRef.current = {
      input: data,
      result
    };
    return result;
  }, [queryClient]);
  const subscribe = useActiveSessionSubscribe(queryClient);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Subscribes to data-bearing events on the `["activeSession"]` cache entry only. */
function useActiveSessionSubscribe(queryClient) {
  const $ = _c(2);
  let t0;
  if ($[0] !== queryClient) {
    t0 = onStoreChange => {
      const scheduled = notifyManager.batchCalls(onStoreChange);
      return queryClient.getQueryCache().subscribe(event => {
        if (event.type !== "updated" && event.type !== "added" && event.type !== "removed") {
          return;
        }
        if (hashKey(event.query.queryKey) === ACTIVE_SESSION_HASH) {
          scheduled();
        }
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/**
 * Whether the active-session query has answered at all.
 *
 * The slice hooks map "not loaded yet" and "no session" to the same `null`.
 * A caller that acts on "no session" — the webcam clears the member's coworking
 * frame — needs to tell them apart, or it acts on the first render of every tab
 * (TX-5190).
 */
export function useActiveSessionLoaded() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  const subscribe = useActiveSessionSubscribe(queryClient);
  let t0;
  if ($[0] !== queryClient) {
    t0 = () => {
      const state = queryClient.getQueryState(ACTIVE_SESSION_KEY);
      return state !== undefined && (state.data !== undefined || state.status === "error");
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const getSnapshot = t0;
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Active session id, or `null` when no session is running. */
export function useActiveSessionId() {
  return useActiveSessionSlice(selectSessionId);
}

/** Whether the active session is paused (on break). `false` when no session. */
export function useActiveSessionIsPaused() {
  return useActiveSessionSlice(selectIsPaused);
}

/** Active session `startedAt` ISO timestamp, or `null` when no session. */
export function useActiveSessionStartedAt() {
  return useActiveSessionSlice(selectStartedAt);
}

/**
 * Reads the current cache entry without subscribing. Use inside callbacks
 * (refetch intervals, schedulers) that need fresh values but must not add a
 * render subscription.
 */
export function getActiveSessionSnapshot(queryClient: QueryClient): ActiveSessionData | null {
  return queryClient.getQueryData<ActiveSessionData | null>(ACTIVE_SESSION_KEY) ?? null;
}
function selectSessionId(data: ActiveSessionData | null): string | null {
  return data?.session?.id ?? null;
}
function selectIsPaused(data: ActiveSessionData | null): boolean {
  return data?.isPaused ?? false;
}
function selectStartedAt(data: ActiveSessionData | null): string | null {
  return data?.session?.startedAt ?? null;
}